import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject, Subject, debounceTime, distinctUntilChanged, takeUntil, Observable, map, firstValueFrom, first } from 'rxjs';
import { Especialidad } from 'src/app/models/Especialidad';
import { Prestacion } from 'src/app/models/Prestacion';
import { Prestador } from 'src/app/models/Prestador';
import { Servicio } from 'src/app/models/Servicio';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderPrestadorService } from 'src/app/services/order-prestador.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { normalizeText } from 'src/app/shared/text.util';

type ShowList = 'especialidad' | 'prestador' | 'prestacion' | 'miPrestador' | null;

@Component({
    selector: 'app-especialidad',
    templateUrl: './especialidad.component.html',
    styleUrls: ['./especialidad.component.css'],
    standalone: false
})
export class EspecialidadComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private _usuarioService = inject(UsuarioService);
  private _orderPrestadorService = inject(OrderPrestadorService);
  private _router = inject(Router);
  private _location = inject(Location);

  error = '';
  loading = false;

  especialidades: Especialidad[] = [];
  prestadores: Prestador[] = [];
  prestaciones: Prestacion[] = [];
  servicios: Servicio[] = [];
  misPrestadores: Prestador[] = [];
  
  especialidadesFiltradas$?: Observable<Especialidad[]>;
  prestadoresFiltrados$?: Observable<Prestador[]>;
  misPrestadoresFiltrados$?: Observable<Prestador[]>;
  prestacionesFiltradas$?: Observable<Prestacion[]>;
  
  searchTerm$ = new BehaviorSubject<string>('');
  searchValue = '';

  especialidadSeleccionada?: number;
  prestadorSeleccionado?: string;
  prestacionSeleccionada?: number;

  showList: ShowList = null;

  // tooltip ayuda (sin tocar el DOM)
  helpOpen = false;

  private destroy$ = new Subject<void>();

  constructor() {
    // id_cliente para la orden, si ya hay usuario cargado
    const u = this._usuarioService.usuario();
    if (u?.id) this._orderPrestadorService.ordenPrestador.id_cliente = u.id;
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();

        // Asegurar id_cliente por si el usuario se cargó luego del constructor
        const u = this._usuarioService.usuario();
        const userId = u?.id!;
        if (u?.id) this._orderPrestadorService.ordenPrestador.id_cliente = u.id;

        const [pres, resServicios, misPres] = await Promise.all([
          firstValueFrom(this._usuarioService.getPrestadoresValidos(true)),
          firstValueFrom(this._usuarioService.getServicios(undefined, true)),
          firstValueFrom(this._orderPrestadorService.getMisPrestadoresXUsuario(userId)),
        ]);

        this._orderPrestadorService.prestadores = pres;
        this.servicios = resServicios;
        this.misPrestadores = misPres;

        // prestadores válidos, ya viene validado del backend con nro_documento completo
        this.prestadores = this._orderPrestadorService.prestadores.filter((item) => {
          const nombreValido = !!item.x_nombre?.trim();
          const apellidoValido = !!item.x_apellido?.trim();
          const nroMatriculaValido = !!item.x_nro_matricula?.trim();
          return nombreValido && apellidoValido && nroMatriculaValido;
        });

        // servicios asociados a prestadores válidos
        const prestadoresIds = new Set(this.prestadores.map((p) => p.id));
        this.servicios = this.servicios.filter((s) => prestadoresIds.has(s.id_usuario));

        // ids únicos disponibles
        const especialidadIds = new Set(this.servicios.map((s) => s.id_especialidad));
        const prestacionIds = new Set(this.servicios.map((s) => s.id_prestacion));

        // filtrar catálogos globales del service
        this.especialidades = this._orderPrestadorService.especialidades.filter((e) => especialidadIds.has(e.id));
        this.prestaciones = this._orderPrestadorService.prestaciones.filter((p) => prestacionIds.has(p.id));

        // armar streams filtrados
        this.filterListEspecialidad();
        this.filterListPrestador();
        this.filterListPrestacion();
        this.filterListMisPrestadores();

        // restaurar selección previa
        const op = this._orderPrestadorService.ordenPrestador;

        if (op.id_especialidad != null) {
          this.especialidadSeleccionada = op.id_especialidad;
          this.showList = 'especialidad';
        } else {
          this.especialidadSeleccionada = undefined;
        }

        if (op.id_prestador != null) {
          this.prestadorSeleccionado = op.id_prestador;
          this.showList = 'prestador';
        } else {
          this.prestadorSeleccionado = undefined;
        }

        if (op.id_prestacion != null) {
          this.prestacionSeleccionada = op.id_prestacion;
          this.showList = 'prestacion';

          // restringir la lista de prestaciones a la especialidad elegida,
          // igual que hace saveEspecialidad() al seleccionarla por primera vez
          if (op.id_especialidad != null) {
            this.filterListPrestacionXEspecialidad(op.id_especialidad);
          }
        } else {
          this.prestacionSeleccionada = undefined;
        }
      } catch (e: any) {
        console.error(e);

        // Si es auth -> sign-in
        if (e?.status === 401 || e?.status === 403) {
          this._router.navigate(['/sign-in']);
          return;
        }

        this.error = e?.error?.message || e?.message || 'No se pudo cargar la pantalla.';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----------------- active helpers -----------------
  get inSearchMode(): boolean {
    return this.showList !== null;
  }

  get currentSearchTitle(): string {
    switch (this.showList) {
      case 'especialidad':
        return 'Buscar por especialidad';
      case 'prestador':
        return 'Buscar por profesional';
      case 'miPrestador':
        return 'Mis médicos';
      case 'prestacion':
        return 'Prestaciones';
      default:
        return 'Buscar turno por';
    }
  }

  backToSearchOptions(): void {
    this.showList = null;
    this.searchValue = '';
    this.searchTerm$.next('');

    this.especialidadSeleccionada = undefined;
    this.prestadorSeleccionado = undefined;
    this.prestacionSeleccionada = undefined;

    this._orderPrestadorService.ordenPrestador.id_especialidad = null;
    this._orderPrestadorService.ordenPrestador.id_prestacion = null;
    this._orderPrestadorService.ordenPrestador.id_prestador = null;
  }
  get showSearchInput(): boolean {
    return this.showList === 'especialidad' || this.showList === 'prestacion' || this.showList === 'prestador';
  }
  get searchPlaceholder(): string {
    switch (this.showList) {
      case 'especialidad':
        return 'Ingresar nombre de la especialidad';
      case 'prestacion':
        return 'Ingresar nombre de la prestación';
      case 'prestador':
        return 'Ingresar apellido o nombre del profesional';
      default:
        return 'Buscar';
    }
  }
  isActiveEspecialidad(id_especialidad: number) {
    return this._orderPrestadorService.ordenPrestador.id_especialidad === id_especialidad;
  }
  isActivePrestador(id_prestador: string) {
    return this._orderPrestadorService.ordenPrestador.id_prestador === id_prestador;
  }
  isActivePrestacion(id_prestacion: number) {
    return this._orderPrestadorService.ordenPrestador.id_prestacion === id_prestacion;
  }
  get especialidadSeleccionadaNombre(): string {
    if (!this.especialidadSeleccionada) return '';

    const esp = this.especialidades.find(e => e.id === this.especialidadSeleccionada);
    return esp?.x_descripcion ?? '';
  }

  // ----------------- guardar selección -----------------
  saveEspecialidad(_event: any, especialidad: Especialidad) {
    this._orderPrestadorService.ordenPrestador.id_especialidad = especialidad.id;
    this._orderPrestadorService.ordenPrestador.id_prestador = null;
    this._orderPrestadorService.ordenPrestador.id_prestacion = null;

    this.especialidadSeleccionada = especialidad.id;
    this.prestacionSeleccionada = undefined;
    this.prestadorSeleccionado = undefined;

    this.searchValue = '';
    this.searchTerm$.next('');

    this.filterListPrestacionXEspecialidad(especialidad.id!);
    this.showList = 'prestacion';
  }

  savePrestador(_event: any, prestador: Prestador) {
    this._orderPrestadorService.ordenPrestador.id_prestador = prestador.id;
    this._orderPrestadorService.ordenPrestador.id_especialidad = null;
    this._orderPrestadorService.ordenPrestador.id_prestacion = null;

    this.prestadorSeleccionado = prestador.id ?? undefined;
    this._router.navigate(['/fecha-turno']);
  }

  savePrestacion(_event: any, prestacion: Prestacion) {
    this._orderPrestadorService.ordenPrestador.id_prestacion = prestacion.id;
    this._orderPrestadorService.ordenPrestador.id_prestador = null;

    this.prestacionSeleccionada = prestacion.id;
    this._router.navigate(['/zona-geografica']);
  }

  // ----------------- filtros -----------------
  filterListEspecialidad(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.especialidadesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        if (t.length < 3) return [];

        // Dividir en palabras (≥3 chars) para búsqueda natural: "estoy gordo" → ["estoy","gordo"]
        const words = t.split(/\s+/).filter(w => w.length >= 3);
        if (words.length === 0) return [];

        return this.especialidades
          .filter((item) => {
            if (item.b_activo === false) return false;
            const desc = normalizeText(item.x_descripcion);
            const tags = normalizeText(item.x_tags ?? '');
            return words.some(word => desc.includes(word) || tags.includes(word));
          })
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  filterListPrestador(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.prestadoresFiltrados$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        if (t.length < 3) return [];

        return this._orderPrestadorService.prestadores
          .filter((item) => {
            const nombreValido = !!item.x_nombre?.trim();
            const apellidoValido = !!item.x_apellido?.trim();
            const nroMatriculaValido = !!item.x_nro_matricula?.trim();

            if (!nombreValido || !apellidoValido || !nroMatriculaValido) {
              return false;
            }

            const nombre = normalizeText(item.x_nombre);
            const apellido = normalizeText(item.x_apellido);
            const matricula = normalizeText(item.x_nro_matricula);
            const nombreCompleto = normalizeText(`${item.x_apellido ?? ''} ${item.x_nombre ?? ''}`);
            const nombreInvertido = normalizeText(`${item.x_nombre ?? ''} ${item.x_apellido ?? ''}`);

            return (
              nombre.includes(t) ||
              apellido.includes(t) ||
              nombreCompleto.includes(t) ||
              nombreInvertido.includes(t) ||
              matricula.includes(t)
            );
          })
          .sort((a, b) =>
            collator.compare(a.x_apellido ?? '', b.x_apellido ?? '')
          );
      })
    );
  }

  filterListPrestacion(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.prestacionesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        return this.prestaciones
          .filter((item) =>
            item.b_activo !== false &&
            normalizeText(item.x_descripcion).includes(t)
          )
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  filterListPrestacionXEspecialidad(id_especialidad: number): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.prestacionesFiltradas$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        return this.prestaciones
          .filter((item) =>
            item.b_activo !== false &&
            item.id_especialidad === id_especialidad &&
            normalizeText(item.x_descripcion).includes(t)
          )
          .sort((a, b) =>
            collator.compare(a.x_descripcion ?? '', b.x_descripcion ?? '')
          );
      })
    );
  }

  filterListMisPrestadores(): void {
    const collator = new Intl.Collator('es', {
      sensitivity: 'base',
      ignorePunctuation: true,
    });

    this.misPrestadoresFiltrados$ = this.searchTerm$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      map((term) => {
        const t = normalizeText(term);

        return this.misPrestadores
          .filter((item) => {
            if (!t) return true;

            const nombre = normalizeText(item.x_nombre);
            const apellido = normalizeText(item.x_apellido);
            const matricula = normalizeText(item.x_nro_matricula);
            const nombreCompleto = normalizeText(`${item.x_apellido ?? ''} ${item.x_nombre ?? ''}`);
            const nombreInvertido = normalizeText(`${item.x_nombre ?? ''} ${item.x_apellido ?? ''}`);

            return (
              nombre.includes(t) ||
              apellido.includes(t) ||
              nombreCompleto.includes(t) ||
              nombreInvertido.includes(t) ||
              matricula.includes(t)
            );
          })
          .sort((a, b) =>
            collator.compare(a.x_apellido ?? '', b.x_apellido ?? '')
          );
      })
    );
  }

  // ----------------- UI -----------------
  selectFilter(option: ShowList) {
    if (this.showList === option) return;

    this.showList = option;
    this.searchValue = '';
    this.searchTerm$.next('');

    if (option === 'especialidad') {
      this._orderPrestadorService.ordenPrestador.id_especialidad = null;
      this._orderPrestadorService.ordenPrestador.id_prestacion = null;
      this._orderPrestadorService.ordenPrestador.id_prestador = null;
      this.filterListEspecialidad();
    }

    if (option === 'prestador') {
      this._orderPrestadorService.ordenPrestador.id_especialidad = null;
      this._orderPrestadorService.ordenPrestador.id_prestacion = null;
      this._orderPrestadorService.ordenPrestador.id_prestador = null;
      this.filterListPrestador();
    }

    if (option === 'miPrestador') {
      this._orderPrestadorService.ordenPrestador.id_especialidad = null;
      this._orderPrestadorService.ordenPrestador.id_prestacion = null;
      this._orderPrestadorService.ordenPrestador.id_prestador = null;
      this.filterListMisPrestadores();
    }

    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 250);
  }

  openhelp() {
    this.helpOpen = !this.helpOpen;
  }

  onSearchKeyup(ev: Event) {
    const value = (ev.target as HTMLInputElement)?.value ?? '';
    this.searchValue = value;
    this.searchTerm$.next(value);
  }

  back() {
    this._location.back();
  }
}
